import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import CancelIcon from '@mui/icons-material/Cancel';
import { getIn, useFormikContext } from 'formik';

import { useStyles } from '~/assets/styles';
import Chip from '~/components/core/Atomic/Chip/Chip';

import { useArrayText } from '../../hooks/useArrayText';
import OverflowTextWithToolTip from '../../OverflowTextWithToolTip';
import { MultiSelectIdLabelTextFieldFormik, useAllOptionOnMultiSelect } from '../../TextFieldFormik';
import FsTooltip from '../FsWrappers/FsTooltip/FsTooltip';
import { Text } from '../TextComponents';

const CHIP_ADDITIONAL_WIDTH = 8 + 27; // padding + chip

const ArrayMultiselectFieldWithChipsFormik = ({
  id,
  label,
  options,
  showValues = false,
  showOnly = false,
  disabled = false,
  allOption,
}) => {
  const { classes } = useStyles();
  const { values, setFieldValue } = useFormikContext();

  if (allOption && !options.includes(allOption)) {
    options.unshift(allOption);
  }

  const selectedValues = getIn(values, id) ?? [];
  useAllOptionOnMultiSelect(selectedValues, id, allOption?.id);

  // The option's label, or the raw value when no option matches. A selected value with no resolvable
  // label still reads as something: reachable both when the option list has not caught up with the
  // selection and when a stored selection names something the current options omit.
  const getOptionLabel = useCallback(
    (value) => options?.find((option) => option?.value === value)?.label ?? value ?? '',
    [options]
  );

  const getSelectedValueLabel = useCallback(
    (value) => (showValues ? value : getOptionLabel(value)),
    [getOptionLabel, showValues]
  );

  const {
    containerRef: chipsContainerRef,
    displayedItems,
    remainingCount,
  } = useArrayText({
    array: selectedValues,
    renderLabel: getSelectedValueLabel,
    labelAdditionalWidth: CHIP_ADDITIONAL_WIDTH,
    showOnly,
  });

  const disableDelete = showOnly || disabled;

  const displayedOptionsWithLabels = displayedItems.map((item) => {
    const option = options.find((option) => option.value === item);
    const label = getSelectedValueLabel(item);
    // The raw value stands in for a missing option's identity too, so an unlabelled chip does not also
    // lose its React key and its delete (which filters `selectedValues` by `value`).
    return { id: item, value: item, ...option, label };
  });

  const handleRemoveChip = (e, value) => {
    e.preventDefault();
    e.stopPropagation();
    setFieldValue(
      id,
      selectedValues.filter((item) => item !== value)
    );
  };

  // Built from the selection, not from the option list: a value with no matching option is still
  // counted by `remainingCount`, so naming it here is what keeps the `+N` and the tooltip agreeing.
  // Labelled via `getOptionLabel` rather than `getSelectedValueLabel` so `showValues` does not reach
  // it: the chips already show the raw values under that prop, which is precisely why the tooltip is
  // the only place the full names are readable.
  const tooltipText = selectedValues
    ?.filter((value) => !displayedItems?.includes(value))
    ?.map((value) => getOptionLabel(value))
    ?.join(', ');

  return (
    <MultiSelectIdLabelTextFieldFormik
      id={id}
      select
      label={label}
      className={classes.textField}
      fullWidth
      showOnly={showOnly}
      options={options}
      disabled={disabled}
      allOptionId={allOption?.id}
      renderValue={() => (
        <span className={classes.chips} ref={chipsContainerRef}>
          {displayedOptionsWithLabels.map((item) => (
            <span key={item.id}>
              <Chip
                className="mb-0"
                data-testid="chip"
                size="small"
                label={<OverflowTextWithToolTip maxWidth="220px">{item.label}</OverflowTextWithToolTip>}
                deleteIcon={
                  disableDelete ? undefined : <CancelIcon onMouseDown={(e) => handleRemoveChip(e, item.value)} />
                }
                onDelete={disableDelete ? undefined : (e) => handleRemoveChip(e, item.value)}
              />
            </span>
          ))}
          <FsTooltip title={tooltipText}>
            {remainingCount ? (
              <Text className="pl-4 pt-4" weight={Text.WEIGHTS.REGULAR} variant={Text.VARIANTS.SM}>
                +{remainingCount}
              </Text>
            ) : (
              <></>
            )}
          </FsTooltip>
        </span>
      )}
    />
  );
};

const OptionPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.any,
  label: PropTypes.string,
});

ArrayMultiselectFieldWithChipsFormik.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.arrayOf(OptionPropType),
  showValues: PropTypes.bool,
  showOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  allOption: OptionPropType,
};

export default ArrayMultiselectFieldWithChipsFormik;
